import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { PaymentOption, TireQuoteItemRecordCustomer, TireQuoteRecordCustomer } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { BookingAddress } from 'noddi-types';
import { NoddiButton, NoddiDialog } from 'noddi-ui-web';
import { customerRoutes, formatCurrencyAmount, formatLicensePlate, getTireBrandName } from 'noddi-util';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BookingAddressPicker } from '../../../../components/Address/BookingAddressPicker';
import { useTireOfferPickerStore } from '../../../../stores/tireOfferPickerStore';
import { getOfferTotalForOption } from '../utils/applyStrategy';
import { AcceptSuccessDialog } from './AcceptSuccessDialog';
import { AcceptResult, BatchResultModal } from './BatchResultModal';

type Selection = {
  offer: TireQuoteRecordCustomer;
  quoteItem: TireQuoteItemRecordCustomer;
};

type ConfirmBatchModalProps = {
  open: boolean;
  selections: Selection[];
  userGroupId: number;
  onCancel: () => void;
  onCompleted: () => void;
};

export const ConfirmBatchModal = ({ open, selections, userGroupId, onCancel, onCompleted }: ConfirmBatchModalProps) => {
  const navigate = useNavigate();
  const selectedAddressId = useTireOfferPickerStore((state) => state.selectedAddressId);
  const setSelectedAddressId = useTireOfferPickerStore((state) => state.setSelectedAddressId);

  const [pickedAddress, setPickedAddress] = useState<BookingAddress | undefined>(undefined);
  const [results, setResults] = useState<AcceptResult[] | null>(null);
  const [successCount, setSuccessCount] = useState<number | null>(null);

  const { mutateAsync: confirmTireQuoteOffer, isPending } = noddiAsync.usePost({
    type: URLKeys.postConfirmTireQuoteOffer
  });

  const requiresAddress = selections.some((selection) => !selection.offer.hasActiveTireHotelContractForWheelSet);
  const isAddressReady = !requiresAddress || selectedAddressId != null;

  const orderTotal = selections.reduce(
    (sum, selection) => sum + getOfferTotalForOption(selection.offer, selection.quoteItem),
    0
  );
  const currency =
    selections[0]?.quoteItem.quoteItemTires[0]?.tireInventory.salesItem.price.priceGross.currency ?? 'NOK';

  const handleAddressChange = (address?: BookingAddress) => {
    setPickedAddress(address);
    setSelectedAddressId(address?.id);
  };

  const handleConfirm = async () => {
    const calls = selections.map(({ offer, quoteItem }) =>
      confirmTireQuoteOffer({
        carTireQuoteItemId: quoteItem.id,
        quoteItemTires: quoteItem.quoteItemTires.map((tire) => ({
          quoteItemTireId: tire.id,
          quantity: tire.quantityDefault ?? 0
        })),
        addressId: offer.hasActiveTireHotelContractForWheelSet ? undefined : selectedAddressId
      })
        .then((response): AcceptResult => {
          if (response.data.paymentMethod === PaymentOption.PAY_BEFORE) {
            const sessionId = response.data.paymentSessionProfile?.sessionId;
            if (sessionId) {
              return { kind: 'pay_before_ok', offer, sessionId };
            }
            return { kind: 'pay_before_ok_no_session', offer };
          }
          return { kind: 'on_delivery_ok', offer };
        })
        .catch((error: unknown): AcceptResult => {
          const message = error instanceof Error ? error.message : t`Something went wrong`;
          return { kind: 'failed', offer, message };
        })
    );

    const settled = await Promise.all(calls);
    invalidateQueryExactMatch({
      urlKey: URLKeys.getCarTireQuotesIgnoringImpersonation,
      input: { userGroupIds: [userGroupId] }
    });

    const failed = settled.filter(
      (result): result is Extract<AcceptResult, { kind: 'failed' }> => result.kind === 'failed'
    );
    const payBefore = settled.filter(
      (result): result is Extract<AcceptResult, { kind: 'pay_before_ok' }> => result.kind === 'pay_before_ok'
    );
    const onDelivery = settled.filter((result) => result.kind === 'on_delivery_ok');

    if (failed.length === 0 && payBefore.length === 1 && onDelivery.length === 0) {
      navigate(customerRoutes.tireOfferConfirmation.getPath({ sessionId: payBefore[0]!.sessionId }));
      onCompleted();
      return;
    }

    if (failed.length === 0 && payBefore.length === 0) {
      setSuccessCount(onDelivery.length);
      return;
    }

    setResults(settled);
  };

  if (successCount !== null) {
    return (
      <AcceptSuccessDialog
        open
        count={successCount}
        onClose={() => {
          setSuccessCount(null);
          onCompleted();
        }}
      />
    );
  }

  if (results) {
    return (
      <BatchResultModal
        results={results}
        onClose={() => {
          setResults(null);
          onCompleted();
        }}
      />
    );
  }

  return (
    <NoddiDialog
      title={plural(selections.length, {
        one: 'Accept # tire offer?',
        other: 'Accept # tire offers?'
      })}
      description={t`We'll order the selected tires from our supplier today. You'll see live status updates as each shipment progresses.`}
      open={open}
      onClose={onCancel}
      maxWidth='md'
      footer={
        <div className='flex flex-col gap-3 border-t border-border bg-muted px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
          <div className='flex flex-col'>
            <div className='text-[11px] font-semibold tracking-wider uppercase'>
              <Trans>Order total</Trans>
            </div>
            <div className='text-xl font-bold tabular-nums'>
              {formatCurrencyAmount({ money: { amount: orderTotal, currency }, decimals: 0 })}
            </div>
          </div>
          <div className='flex gap-2'>
            <NoddiButton variant='outline' onClick={onCancel} disabled={isPending} className='flex-1 sm:flex-none'>
              <Trans>Back</Trans>
            </NoddiButton>
            <NoddiButton
              // oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleConfirm(); React discards the returned promise, so a rejection is unhandled unless the body catches
              onClick={handleConfirm}
              loading={isPending}
              disabled={!isAddressReady}
              startIcon='Check'
              className='flex-1 sm:flex-none'
            >
              <Trans>Confirm and order</Trans>
            </NoddiButton>
          </div>
        </div>
      }
    >
      <div className='flex flex-col gap-4 px-1 pb-2'>
        <div className='flex flex-col divide-y divide-border'>
          {selections.map(({ offer, quoteItem }) => {
            const headlineTire = quoteItem.quoteItemTires[0];
            const total = getOfferTotalForOption(offer, quoteItem);
            return (
              <div key={offer.id} className='flex items-center gap-3 py-2.5'>
                {headlineTire?.tireInventory.imageTire?.url ? (
                  <img
                    src={headlineTire.tireInventory.imageTire.url}
                    alt=''
                    className='size-8 shrink-0 rounded-sm object-contain'
                  />
                ) : (
                  <div className='size-8 shrink-0 rounded-sm bg-muted' />
                )}
                <div className='min-w-0 flex-1'>
                  <div className='truncate text-sm font-semibold'>
                    {offer.car.make} {offer.car.model}
                    {offer.car.licensePlateNumber && (
                      <span className='ml-2 font-mono text-[11px] font-normal text-muted-foreground'>
                        {formatLicensePlate(offer.car.licensePlateNumber)}
                      </span>
                    )}
                  </div>
                  <div className='truncate text-xs text-muted-foreground'>
                    {headlineTire && getTireBrandName(headlineTire.tireInventory.tire)}{' '}
                    {headlineTire?.tireInventory.tire.model ?? ''}
                  </div>
                </div>
                <div className='text-sm font-semibold text-primary tabular-nums'>
                  {formatCurrencyAmount({ money: { amount: total, currency }, decimals: 0 })}
                </div>
              </div>
            );
          })}
        </div>

        {requiresAddress && (
          <div className='flex flex-col gap-2 rounded-lg border border-warning50 bg-warning50/50 p-4'>
            <div className='text-sm font-semibold text-warningDark'>
              <Trans>Where should we deliver?</Trans>
            </div>
            <div className='text-xs text-muted-foreground'>
              <Trans>Some of these offers need a delivery address because the wheel set isn't in our storage.</Trans>
            </div>
            <BookingAddressPicker
              selectedAddress={pickedAddress}
              setSelectedAddress={handleAddressChange}
              keepPopoverBelowOnPhones={false}
            />
          </div>
        )}
      </div>
    </NoddiDialog>
  );
};
